import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { PlantillaIndexShell } from '../components/PlantillaIndexShell';
import type { IndexQueryState } from '../../../../components/index-page/types';
import { ROW_ORDINAL, type PlantillaColumnDef } from '../components/PlantillaIndexTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { orgUnitsApi } from '../../organization/organizationApi';
import { positionsApi } from '../../positions/positionsApi';
import { PlantillaPositionModal } from '../components/PlantillaPositionModal';
import { plantillaApi } from '../plantillaApi';
import type { PlantillaPositionListItem } from '../plantillaTypes';

/** The staffing pattern — the posts the organization is authorized to fill. */
export function PlantillaIndexPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<PlantillaPositionListItem | null>(null);
  /*
    Null closes the form; an object opens it, carrying the unit the list was filtered to.
    A boolean could not: the seed has to be captured when Create is pressed, because that is
    when the filter is known.
  */
  const [creating, setCreating] = useState<{ unitId: string | null } | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    position: PlantillaPositionListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    WHAT THIS MODULE CANNOT BE USED WITHOUT — a designation to authorize, and a unit to
    authorize it in. `<PlantillaPositionModal>` requires both pickers and loads both feeds, so
    an empty catalog on either side is a post nobody can create.

    ORDER IS THE CHAIN, and the banner names one at a time: the POSITION catalog leads because
    it is the deeper of the two — a title carries the qualification standards and the salary
    grade, and the positions page in turn states that the salary schedule has to come first.
    Units are the shallower requirement, so they are named once the titles exist.

    This page is itself the link somebody arrives on from Personnel, which declares a vacant
    post here as its own single prerequisite. Each page states ITS OWN requirement and links to
    it, so the chain walks itself and no page has to name a catalog it holds no key for.

    Seeded TRUE and a failed feed leaves it true, for the reason the shared shell documents: a
    403 is not evidence that a catalog is empty.
  */
  const [ready, setReady] = useState({ positions: true, units: true });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      positionsApi.options(controller.signal),
      orgUnitsApi.options(undefined, controller.signal),
    ])
      .then(([positions, units]) => {
        if (controller.signal.aborted) return;

        setReady({ positions: positions.length > 0, units: units.length > 0 });
      })
      .catch(() => undefined);

    return () => controller.abort();

    // Re-asked after a write, so filling the last empty catalog clears the banner without a
    // reload — the reader fixed it, and the page should agree.
  }, [reloadKey]);

  const fetchPositions = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      plantillaApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          /*
            Unit order by default, because the Unit column MERGES adjacent rows: the merge
            groups neighbours, so on a list ordered by title the same unit would produce a
            separate cell every time it reappeared. A reader who sorts by another column gets
            that order and the merge breaks into runs of one, which is honest — the rows are
            no longer grouped.
          */
          sort: query.sort ?? 'orgUnitName:asc',
          status: query.facets.status?.join(','),
          unit: query.facets.unit?.join(','),
          vacancy: query.facets.vacancy?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (position: PlantillaPositionListItem): MenuItem[] => [
      {
        key: `restore-${position.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'plantilla.delete',
        disabledReason: position.isActive ? 'This position is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', position }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await plantillaApi.deactivate(confirming.position.id);
      else await plantillaApi.activate(confirming.position.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <PlantillaIndexShell<PlantillaPositionListItem>
        module="plantilla"
        breadcrumb={['Human Resources', 'Plantilla']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'unit', label: 'Unit' },
          { key: 'vacancy', label: 'Vacancy' },
        ]}
        auditEntityTypes={['PlantillaPosition', 'PlantillaItem']}
        rowKey={(position) => position.id}
        fetch={fetchPositions}
        reloadKey={reloadKey}
        searchPlaceholder="Search positions or item numbers"
        emptyMessage="No positions yet. Add the posts this organization is authorized to fill."
        /*
          See `ready`. Both carry the key their own page needs — `plantilla.create` implies both
          reads, so a reader who could fill the post form gets both links, and one who could not
          is still told what is missing.
        */
        prerequisites={[
          {
            label: 'positions',
            met: ready.positions,
            to: '/organization/settings/positions',
            permission: 'positions.read',
          },
          {
            label: 'organizational units',
            met: ready.units,
            to: '/organization/chart',
            permission: 'org-units.read',
          },
        ]}
        /*
          Seeded from the filter when the reader has narrowed to exactly ONE unit. Two or more
          is not an answer, so the picker opens empty rather than guessing which of them the
          post belongs to.
        */
        onCreate={({ unitIds }) =>
          setCreating({ unitId: unitIds.length === 1 ? (unitIds[0] ?? null) : null })
        }
        createPermission="plantilla.create"
        /*
          The anchor opens the post rather than its edit form: the slots are the substance
          here, and they live on the profile. `openPermission` is the read key because that is
          what opening it actually needs.
        */
        onRowOpen={(position) => navigate(`/plantilla/${position.id}`)}
        openPermission="plantilla.read"
        rowActions={rowActions}
        onEdit={(position) => setEditing(position)}
        editPermission="plantilla.update"
        onDelete={(position) => setConfirming({ kind: 'deactivate', position })}
        deletePermission="plantilla.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(position) =>
          !position.isActive
            ? 'This position is already deleted.'
            : position.filledSlots > 0
              ? `Vacate its ${position.filledSlots} filled slot${position.filledSlots === 1 ? '' : 's'} first.`
              : null
        }
        onClearData={() => plantillaApi.clear()}
        clearDataPermission="plantilla.clear-data"
        otherInfo={(position) => position}
      />

      {creating !== null || editing !== null ? (
        <PlantillaPositionModal
          open
          editing={editing}
          defaultUnitId={creating?.unitId ?? null}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
          /*
            A create opens the post's profile, because the items are the substance and they
            live there — the reader authorized a headcount and the next thing they want is the
            list of what it produced. An edit stays on the list, which is where it was made.
          */
          onCreated={(position) => navigate(`/plantilla/${position.id}`)}
          onSaved={() => {
            setCreating(null);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete position' : 'Restore position'}
          danger={confirming.kind === 'deactivate'}
          loading={running}
          id={`plantilla-position-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.position.title}" and its ${confirming.position.authorizedSlots} slot${confirming.position.authorizedSlots === 1 ? '' : 's'} leave the staffing pattern. The record is kept, and its code stays reserved forever.`
                  : `"${confirming.position.title}" returns to the staffing pattern with no slots — restore those one at a time, so a headcount is never re-authorized by accident.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The staffing pattern's own columns — the plantilla shell's, not the shared table's.
 *
 * Read down a column rather than across a row: how many items a post has, how many are
 * vacant, what it currently pays. That is why this surface opens in table view.
 */
const COLUMNS: PlantillaColumnDef<PlantillaPositionListItem>[] = [
  {
    /*
      The unit, merged across the run of posts that sit in it — one cell per unit rather than
      the same name repeated down forty rows. It reads upright in a narrow column, so labelling
      the run costs the table almost no width.

      It is NOT the anchor: a merged cell belongs to the run it covers rather than to any one
      record in it, so the table skips it when deciding the row's click target.
    */
    key: 'orgUnitName',
    header: 'Unit',
    width: 44,
    mergeKey: (position) => position.orgUnitId,
    sideways: true,
    render: (position) => position.orgUnitName,
  },
  {
    /*
      The post's number WITHIN its unit — 1, 2, 3 and back to 1 at the next unit. Distinct
      from the `#` gutter, which numbers rows down the page: this one answers "which post of
      this unit is this", which is the question a staffing pattern is read with.

      Not sortable, and it carries no server field: it is a position in the rendered run, so
      ordering the list by it would be ordering by the order it already has.
    */
    key: 'unitIndex',
    header: '#',
    width: 48,
    align: 'right',
    groupCounter: true,
    /*
      Set like the `#` gutter rather than through <TextCell>: both columns hold an ordinal,
      and an ordinal is how you refer to a row rather than something the row tells you — so
      it is muted and a size down from the data beside it, and the two read as one kind of
      value. ROW_ORDINAL is the single definition of that look.
    */
    render: (_position, withinUnit) => <span className={ROW_ORDINAL}>{withinUnit}</span>,
  },
  {
    key: 'title',
    header: 'Position',
    width: 240,
    flex: true,
    sortable: true,
    summary: true,
    render: (position) => <TextCell value={position.title} />,
  },
  {
    /*
      The CATALOG title's code, not the post's own id. The id is PROG-26001 and belongs to
      this one post; the code is the designation's, which is what a reader scans a staffing
      pattern by. The id is on the post's profile, where it identifies the record.
    */
    key: 'positionCode',
    header: 'Code',
    width: 110,
    summary: true,
    render: (position) => <TextCell value={position.positionCode} mono />,
  },
  {
    key: 'salaryGrade',
    header: 'Salary Grade',
    width: 120,
    /*
      Left, unlike the numeric columns beside it. "SG 11" is a label, not a quantity — nobody
      compares magnitudes down it or adds it up, and right-aligning it only pushed the value
      away from the header naming it.
    */
    sortable: true,
    render: (position) => (
      <TextCell value={position.salaryGrade == null ? null : `SG ${position.salaryGrade}`} mono />
    ),
  },
  {
    /*
      Its own column rather than folded into the grade: a step is what the post is authorized
      at within the grade, and the two are compared separately down a plantilla.
    */
    key: 'salaryStep',
    header: 'Step Increment',
    width: 120,
    align: 'right',
    render: (position) => (
      <TextCell
        value={position.salaryStep == null ? null : String(position.salaryStep)}
        mono
        numeric
      />
    ),
  },
  {
    /*
      What the grade and step currently pay, from the most recently effective tranche — read
      on every list rather than stored, so a new tranche reprices the whole pattern at once.
    */
    key: 'monthlyAmount',
    header: 'Amount',
    width: 130,
    align: 'right',
    render: (position) => <TextCell value={formatAmount(position.monthlyAmount)} mono numeric />,
  },
  {
    // The three counts read as one question — what this post holds — so they sit under one
    // header. Declared consecutively, which is what a spanning cell requires.
    group: 'Inventory',
    key: 'vacantSlots',
    header: 'Vacant',
    width: 90,
    align: 'right',
    render: (position) => <TextCell value={String(position.vacantSlots)} mono numeric />,
  },
  {
    // "Fulfilled" is the reader's word for an occupied item. Zero everywhere until the
    // Personnel phase gives an item someone to hold, and honest at zero.
    group: 'Inventory',
    key: 'filledSlots',
    header: 'Fulfilled',
    width: 90,
    align: 'right',
    render: (position) => <TextCell value={String(position.filledSlots)} mono numeric />,
  },
  {
    // The live items under the post — the count the Slots field on the create form produced.
    group: 'Inventory',
    key: 'authorizedSlots',
    header: 'Items',
    width: 80,
    align: 'right',
    render: (position) => <TextCell value={String(position.authorizedSlots)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (position) =>
      position.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];

/**
 * A monthly rate, or an em-dash when the post carries no grade.
 *
 * Grouped and to two decimals, through the browser's own formatter rather than a hand-rolled
 * one — and with no currency symbol, because the schedule records an amount and the product
 * has never said which currency anywhere else.
 */
function formatAmount(amount: number | null): string | null {
  if (amount == null) return null;

  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

